<template>
    <div class="about-container">
        <div class="logo-wrap">
            <img :src="swayLogo" alt="Sway">
        </div>
        <div class="version">Version {{ version }}</div>
        <div class="country-prompt">Choose your country</div>
        <button type="button" class="mobile-btn" @click="selectedCountry = 'us'">USA</button>
        <button type="button" class="mobile-btn" @click="selectedCountry = 'europe'">International</button>

        <!-- US content -->
        <div v-if="selectedCountry === 'us'" class="country-content">
            <div class="info-title">USA Product Information</div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <div class="product-img">
                        <svg width="30px" height="24px" viewBox="0 0 56 50" xmlns="http://www.w3.org/2000/svg">
                            <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                <polygon stroke="#000000" stroke-width="2" fill="#000000" fill-rule="nonzero"
                                                  points="1 48.8492124 1 28.0427002 7.38687518 18.1348372 13.5448897 27.4615285 20.0368215 18.1348372 26.5517454 28.0427002 34.5098711 18.1348372 41.1206548 28.0427002 41.1206548 1 54.0528744 1 54.0528744 48.8492124"></polygon>
                            </g>
                        </svg>
                    </div>
                </div>
                <div class="product-info-text">
                    <div>Manufacturer:</div>
                    <div>Sway Medical, Inc.</div>
                    <div>32 S Lewis Avenue, Tulsa, OK 74101</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <img class="product-img" :src="images.ref" alt="">
                </div>
                <div class="product-info-text">
                    <div>Device Name:</div>
                    <div>Sway System, version {{ version }}</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo"><span style="font-size: 12px">UDI:</span></div>
                <div class="product-info-text">00868168000306</div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <div class="product-img">
                        <svg width="30px" height="24px" viewBox="0 0 56 50" xmlns="http://www.w3.org/2000/svg">
                            <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                <polygon stroke="#000000" stroke-width="2" fill-rule="nonzero"
                                                  points="1 48.8492124 1 28.0427002 7.38687518 18.1348372 13.5448897 27.4615285 20.0368215 18.1348372 26.5517454 28.0427002 34.5098711 18.1348372 41.1206548 28.0427002 41.1206548 1 54.0528744 1 54.0528744 48.8492124"></polygon>
                            </g>
                        </svg>
                    </div>
                </div>
                <div class="product-info-text">
                    <div>Release Date:</div>
                    <div>{{ releaseDate }}</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <img class="product-img" :src="images.rxOnly" alt="">
                </div>
                <div class="product-info-text">
                    <div>Prescription Only</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <img class="product-img" :src="images.instructions" alt="">
                </div>
                <div class="product-info-text">
                    <div>Consult Instructions for Use or User Manual</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <img class="product-img" :src="images.warning" alt="">
                </div>
                <div class="product-info-text">
                    <div>See Warnings and Contraindications</div>
                </div>
            </div>

            <div>
                <div class="section-title">Device Description</div>
                <div class="section-content">
                    Sway System is a Balance and Cognitive function analysis system intended to provide interactive tests for a patient in a non-clinical or clinical setting to assess balance and cognitive status (e.g., attention, concentration, short term memory/recall, motor control). It consists of sensors to measure balance deficits, and a mobile device/software for interactive on-screen tests and to provide cognitive/motion control tests and comparative assessment results. This device is typically used to screen for and/or monitor patients at risk of fall, who suffered a concussion, or who are affected by dementia or neuromuscular disorders (e.g., Alzheimer&rsquo;s disease, Parkinson&rsquo;s disease).
                    <br/><br/>
                    Sway System is a platform with tools that measures balance, cognition, and functional movement using a mobile device without the need for additional hardware or software. The mobile applications running Apple iOS/iPadOS and Android operating systems utilize the built-in hardware that exists in smart phone or tablet devices to provide accurate measurements that can be tracked over time.
                </div>
            </div>

            <div>
                <div class="section-title">Intended Use</div>
                <div class="section-content">
                    The <span style="font-weight: bold">Sway Balance</span>&trade; is intended for use to assess sway as an indicator of balance. Individual suitability for assessment must be judged on a case-by-case basis, by a qualified individual including those certified and/or licensed in their state to prescribe and/or use balance devices such as certified athletic trainers and coaches, physical therapists, nurses and physicians. Conditions affecting postural sway include nausea, headache, orthopedic injury, ear infection, medications, head injury, dehydration and fatigue. The Sway Balance&trade; System can be used wherever an iOS mobile operating device is available.
                    <br/><br/>
                    The <span style="font-weight: bold">Sway Cognitive</span> is intended for use as a computerized cognitive assessment aid to be administered by a qualified health profession to assess cognitive function.
                    <br/><br/>
                    The <span style="font-weight: bold">Sway System Sports Plus</span> is intended for use as a computerized cognitive assessment test to aid in the assessment and management of concussion in individuals ages 18-24.
                </div>
            </div>

            <div>
                <div class="section-title">Warnings and Precautions</div>
                <div class="section-content">
                    <div style="font-weight: bold">Rx Only</div>
                    
                    <ul>
                        <li>Sway System is not intended to diagnose or treat any medical condition or disease.</li>
                        <li>Sway System is designed to provide a portable screening tool to aid in the assessment and management of concussion and in no way should it replace an evaluation by a medical professional.</li>
                        <li>Always ensure a safe and appropriate testing environment to eliminate the risk of fall and related injury. Supervision of testing should be provided to ensure proper test administration and reduce potential fall or injury in balance-compromised and elderly populations.</li>
                        <li>Sway System is a medical device and should only be administered with a full understanding of the testing procedure after reading these instructions for use. Testing should be performed in a controlled environment to ensure test repeatability. Improper testing conditions include:
                            <ul>
                                <li>Unstable or grass surfaces</li>
                                <li>Wet or slippery surfaces</li>
                                <li>High noise level environments</li>
                                <li>Cluttered or hazardous environments</li>
                                <li>Distracted or socially interactive environments.</li>
                            </ul>
                            Testing in an improper environment may lead to inaccurate results.</li>
                        <li>If you suspect traumatic brain injury (TBI) or concussion, especially following a potential injury, seek medical attention immediately.</li>
                        <li>Attempting a balance test with suspected balance impairment will increase the risk for fall injury and should not be performed without supervision. Do not perform a balance test if you have been injured without first seeking medical attention.</li>
                        <li>Only use Sway System on approved devices. We recommend that you use devices that are identified on Sway&rsquo;s list of approved devices. It is unknown whether additional devices from manufacturers not on the approved list provide consistent results, because these devices have not been verified by Sway.</li>
                    </ul>
                </div>
            </div>

            <div>
                <div class="section-title">Instructions for Use</div>
                <div class="section-content">
                    Tutorial videos are below.
                    <br/><br/>
                    Additional information, including the Sway System User Manual can be obtained at <a href="https://healthyroster.helpjuice.com/new-sway-help-center">Sway Help Center</a>
                </div>
                <div class="help-videos">
                    <div v-for="vid in helpVideos" :key="vid.url" class="help-video">
                        <div>{{ vid.label }}</div>
                        <iframe :src="vid.url" loading="lazy" allowfullscreen></iframe>
                    </div>
                </div>
            </div>
        </div>

        <!-- Europe / Australia content -->
        <div v-if="selectedCountry === 'europe'" class="country-content">
            <div class="info-title">International Product Information</div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <div class="product-img">
                        <svg width="30px" height="24px" viewBox="0 0 56 50" xmlns="http://www.w3.org/2000/svg">
                            <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                <polygon stroke="#000000" stroke-width="2" fill="#000000" fill-rule="nonzero"
                                                  points="1 48.8492124 1 28.0427002 7.38687518 18.1348372 13.5448897 27.4615285 20.0368215 18.1348372 26.5517454 28.0427002 34.5098711 18.1348372 41.1206548 28.0427002 41.1206548 1 54.0528744 1 54.0528744 48.8492124"></polygon>
                            </g>
                        </svg>
                    </div>
                </div>
                <div class="product-info-text">
                    <div>Manufacturer:</div>
                    <div>Sway Medical, Inc.</div>
                    <div>32 S Lewis Avenue, Tulsa, OK 74101</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <img class="product-img" :src="images.ref" alt="">
                </div>
                <div class="product-info-text">
                    <div>Device Name:</div>
                    <div>Sway System, version {{ version }}</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo"><span style="font-size: 12px">UDI:</span></div>
                <div class="product-info-text">00868168000306</div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <div class="product-img">
                        <svg width="30px" height="24px" viewBox="0 0 56 50" xmlns="http://www.w3.org/2000/svg">
                            <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                <polygon stroke="#000000" stroke-width="2" fill-rule="nonzero"
                                                  points="1 48.8492124 1 28.0427002 7.38687518 18.1348372 13.5448897 27.4615285 20.0368215 18.1348372 26.5517454 28.0427002 34.5098711 18.1348372 41.1206548 28.0427002 41.1206548 1 54.0528744 1 54.0528744 48.8492124"></polygon>
                            </g>
                        </svg>
                    </div>
                </div>
                <div class="product-info-text">
                    <div>Release Date:</div>
                    <div>{{ releaseDate }}</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <img class="product-img" :src="images.rxOnly" alt="">
                </div>
                <div class="product-info-text">
                    <div>Prescription Only</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <img class="product-img" :src="images.instructions" alt="">
                </div>
                <div class="product-info-text">
                    <div>Consult Instructions for Use or User Manual</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <img class="product-img" :src="images.warning" alt="">
                </div>
                <div class="product-info-text">
                    <div>See Warnings and Contraindications</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <img class="product-img" :src="images.ecRep" alt="">
                </div>
                <div class="product-info-text">
                    <div>CMC Medical Devices &amp; Drugs S.L.</div>
                    <div>C/Horacio Lengo N&deg; 18</div>
                    <div>CP 29006, M&aacute;laga Spain</div>
                </div>
            </div>

            <div class="product-section-container">
                <div class="product-info-logo">
                    <div style="font-size: 10px; text-align: center">Australian Sponsor:</div>
                </div>
                <div class="product-info-text">
                    <div>PlexusRA</div>
                    <div>3 Oxford Drive, Thurgoona</div>
                    <div>NSW 2640 Australia</div>
                </div>
            </div>


            <div>
                <div class="section-title">Device Description</div>
                <div class="section-content">
                    Sway System is a Balance and Cognitive function analysis system intended to provide interactive tests for a patient in a non-clinical or clinical setting to assess balance and cognitive status (e.g., attention, concentration, short term memory/recall, motor control). It consists of sensors to measure balance deficits, and a mobile device/software for interactive on-screen tests and to provide cognitive/motion control tests and comparative assessment results. This device is typically used to screen for and/or monitor patients at risk of fall, who suffered a concussion, or who are affected by dementia or neuromuscular disorders (e.g., Alzheimer’s disease, Parkinson’s disease).
                    <br/><br/>
                    Sway System is a platform with tool that measures balance, cognition, and functional movement using a mobile device without the need for additional hardware or software. The mobile applications running Apple iOS/iPadOS and Android operating systems utilize the built-in hardware that exists in smart phone or tablet devices to provide accurate measurements that can be tracked over time.
                </div>
            </div>

            <div>
                <div class="section-title">Intended Use</div>
                <div class="section-content">
                    The <span style="font-weight: bold">Sway Balance</span>&trade; is intended for use to assess sway as an indicator of balance. Individual suitability for assessment must be judged on a case-by-case basis, by a qualified individual including those certified and/or licensed in their state to prescribe and/or use balance devices such as certified athletic trainers and coaches, physical therapists, nurses and physicians. Conditions affecting postural sway include nausea, headache, orthopedic injury, ear infection, medications, head injury, dehydration and fatigue. The Sway Balance&trade; System can be used wherever an iOS mobile operating device is available.
                    <br/><br/>
                    The <span style="font-weight: bold">Sway Cognitive</span> is intended for use as a computerized cognitive assessment aid to be administered by a qualified health profession to assess cognitive function.
                </div>
            </div>

            <div>
                <div class="section-title">Warnings and Precautions</div>
                <div class="section-content">
                    <ul>
                        <li>Sway System is not intended to diagnose or treat any medical condition or disease.</li>
                        <li>Sway System is designed to provide a portable screening tool to aid in the assessment and management of concussion and in no way should it replace an evaluation by a medical professional.</li>
                        <li>Always ensure a safe and appropriate testing environment to eliminate the risk of fall and related injury. Supervision of testing should be provided to ensure proper test administration and reduce potential fall or injury in balance-compromised and elderly populations.</li>
                        <li>Sway System is a medical device and should only be administered with a full understanding of the testing procedure after reading these instructions for use. Testing should be performed in a controlled environment to ensure test repeatability. Improper testing conditions include:
                            <ul>
                                <li>Unstable or grass surfaces</li>
                                <li>Wet or slippery surfaces</li>
                                <li>High noise level environments</li>
                                <li>Cluttered or hazardous environments</li>
                                <li>Distracted or socially interactive environments.</li>
                            </ul>
                            Testing in an improper environment may lead to inaccurate results.</li>
                        <li>If you suspect traumatic brain injury (TBI) or concussion, especially following a potential injury, seek medical attention immediately.</li>
                        <li>Attempting a balance test with suspected balance impairment will increase the risk for fall injury and should not be performed without supervision. Do not perform a balance test if you have been injured without first seeking medical attention.</li>
                        <li>Only use Sway System on approved devices. We recommend that you use devices that are identified on Sway&rsquo;s list of approved devices. It is unknown whether additional devices from manufacturers not on the approved list provide consistent results, because these devices have not been verified by Sway.</li>
                    </ul>
                </div>
            </div>

            <div>
                <div class="section-title">Instructions for Use</div>
                <div class="section-content">
                    Tutorial videos are below.
                    <br/><br/>
                    Additional information, including the Sway System User Manual can be obtained at <a href="https://healthyroster.helpjuice.com/new-sway-help-center">Sway Help Center</a>
                </div>
                <div class="help-videos">
                    <div v-for="vid in helpVideos" :key="vid.url" class="help-video">
                        <div>{{ vid.label }}</div>
                        <iframe :src="vid.url" loading="lazy" allowfullscreen></iframe>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import swayLogo from '@/assets/images/sway-logo.png';
import refImg from '@/assets/images/about/ref.png';
import rxOnly from '@/assets/images/about/rx-only.png';
import instructions from '@/assets/images/about/instructions.png';
import warning from '@/assets/images/about/warning.png';
import ecRep from '@/assets/images/about/ec-rep.png';

export default {
    name: 'About',
    data() {
        return {
            swayLogo,
            images: {
                ref: refImg,
                rxOnly,
                instructions,
                warning,
                ecRep
            },
            selectedCountry: null,
            versionDates: [
                { version: '5.0.0', date: '15 June 2021' },
                { version: '5.0.1', date: '25 June 2021' },
                { version: '5.1.0', date: '5 August 2021' },
                { version: '5.1.1', date: '8 August 2021' },
                { version: '5.1.2', date: '10 August 2021' },
                { version: '5.1.3', date: '14 August 2021' },
                { version: '5.1.4', date: '16 August 2021' },
                { version: '5.2.0', date: '14 October 2021' },
                { version: '5.2.1', date: '26 October 2021' },
                { version: '5.2.2', date: '17 December 2021' },
                { version: '5.3.0', date: '22 March 2022' },
                { version: '5.3.1', date: '17 March 2022' },
                { version: '5.3.2', date: '17 May 2022' },
                { version: '5.4.0', date: '17 July 2022' },
                { version: '5.4.1', date: '14 July 2022' },
                { version: '5.4.2', date: '19 July 2022' },
                { version: '5.4.3', date: '25 July 2022' },
                { version: '5.4.4', date: '27 July 2022' },
                { version: '5.4.5', date: '13 September 2022' },
                { version: '5.4.6', date: '17 January 2023' },
                { version: '5.5.0', date: '27 March 2023' },
                { version: '5.5.1', date: '30 March 2023' },
                { version: '5.5.2', date: '1 April 2023' },
                { version: '5.5.3', date: '4 April 2023' },
                { version: '5.5.4', date: '17 April 2023' },
                { version: '5.5.5', date: '17 November 2023' },
                { version: '5.5.6', date: '27 November 2023' },
                { version: '5.5.6', date: '27 November 2023' },
                { version: '5.5.7', date: '16 January 2024' },
                { version: '5.5.8', date: '16 January 2024' },
                { version: '5.5.9', date: '19 March 2024' },
                { version: '5.5.10', date: '13 May 2024' },
                { version: '5.5.11', date: '21 June 2024' },
                { version: '6.0.0', date: '21 May 2025' },
                { version: '6.0.1', date: '22 May 2025' },
                { version: '6.0.2', date: '16 June 2025' },
                { version: '6.0.3', date: '30 July 2025' },
                { version: '6.0.4', date: '8 August 2025' },
                { version: '6.0.5', date: '31 August 2025' },
                { version: '6.0.6', date: '19 January 2026' },
                { version: '6.1.0', date: '1 June 2026' },
                { version: '6.1.1', date: '6 July 2026' }
            ],
            helpVideos: [
                { label: 'Getting Started',               url: 'https://www.youtube.com/embed/YaRb5r34l94' },
                { label: 'Add a Profile',                 url: 'https://www.youtube.com/embed/8Z7z0IWtk' },
                { label: 'Balance Testing',               url: 'https://www.youtube.com/embed/FsCkIIsP0e0' },
                { label: 'Reaction Time Testing',         url: 'https://www.youtube.com/embed/RuTK4EbY8p0' },
                { label: 'Establishing a Baseline',       url: 'https://www.youtube.com/embed/zz7IpQtDQzI' },
                { label: 'Creating an Event',             url: 'https://www.youtube.com/embed/hKHVTS3nyI0' },
                { label: 'Scoring System',                url: 'https://www.youtube.com/embed/f4nLQ6YyTb0' },
                { label: 'Profile Overview',              url: 'https://www.youtube.com/embed/MURRQyiXsUY' },
                { label: 'Baseline Reset',                url: 'https://www.youtube.com/embed/s5emL4iTDNQ' },
                { label: 'Creating a Code',               url: 'https://www.youtube.com/embed/S6rGbSWCzpY' },
                { label: 'Performing a Baseline Session', url: 'https://www.youtube.com/embed/e6iItEGhvNM' },
                { label: 'Accelerometer Verification',    url: 'https://www.youtube.com/embed/vQMGKdBZbSk' },
                { label: 'Inspection Time Testing',       url: 'https://www.youtube.com/embed/cCKbRf5RxlA' },
                { label: 'Impulse Control Testing',       url: 'https://www.youtube.com/embed/NDH7CRP9pYY' },
                { label: 'Memory Testing',                url: 'https://www.youtube.com/embed/tkDbL8iIH3A' },
                { label: 'At Home Testing',               url: 'https://www.youtube.com/embed/b449V9JOsJU' },
                { label: 'Remote Symptom Tracking',       url: 'https://www.youtube.com/embed/94HilvbCNGs' }
            ]
        };
    },
    computed: {
        version() {
            return this.$route.query.v || '-';
        },
        releaseDate() {
            const entry = this.versionDates.find(e => e.version === this.$route.query.v);
            return entry ? entry.date : '6 July 2026'; // TODO: make this update to last by default
        }
    }
};
</script>

<style scoped>
.about-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 16px 32px;
    color: #757575;
    font-size: 12px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}
.logo-wrap {
    max-height: 50px;
    margin: 20px;
}
.logo-wrap img {
    max-width: 100%;
    max-height: 50px;
}
.version {
    margin: 20px;
    font-size: 16px;
    font-weight: bold;
}
.country-prompt {
    margin-top: 20px;
}
.mobile-btn {
    background-color: #34d3fe;
    padding: 10px 30px;
    min-width: 50%;
    color: white;
    font-weight: 600;
    text-align: center;
    border-radius: 5px;
    font-size: 16px;
    margin-top: 10px;
    border: none;
    cursor: pointer;
    font-family: inherit;
}
.country-content {
    margin-top: 30px;
    width: 100%;
    max-width: 600px;
}
.info-title {
    color: #0F4761;
    white-space: pre-line;
    margin-bottom: 10px;
    font-size: 16px;
}
.product-section-container {
    display: flex;
    margin: 10px;
    align-items: center;
}
.product-info-logo {
    width: 70px;
    display: flex;
    justify-content: center;
}
.product-info-text {
    font-size: 12px;
}
.product-img {
    max-width: 80%;
    max-height: 30px;
}
.section-title {
    color: #0F4761;
    margin-bottom: 10px;
    margin-top: 30px;
    font-size: 16px;
}
.section-content {
    font-size: 11px;
    color: #757575;
    white-space: pre-line;
}
.section-content ul {
    margin: 8px 0;
    padding-left: 20px;
}
.help-videos {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;
}
.help-video {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;
}
.help-video iframe {
    margin-top: 10px;
    width: 80%;
    max-width: 300px;
    height: 150px;
    border: 0;
}
</style>
